import React from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import MarkupPreview from '../../../../atoms/MarkupPreview'

// Shared row renderer for Wealthbox notes. Used by:
//   - wealthboxList/WealthboxNotesContent.jsx (notes-preset standalone list)
// Following the same shape as WealthboxTaskRow so visual rhythm stays
// consistent across wealthbox-list presets.

// See WealthboxTaskRow for the rationale behind the "first + N more" hint.
function linkedContactName (note) {
  const links = Array.isArray(note?.linked_to) ? note.linked_to : []
  const first = links[0]?.name
  if (!first) return null
  return links.length > 1 ? `${first} (+${links.length - 1})` : first
}

function WealthboxNoteRow ({ note, previewLength = 200, onClick }) {
  const dateFormatted = note.created_at ? dayjs(note.created_at).format('MMM D, YYYY') : null
  // Notes don't have a `category` field on Wealthbox - they use `tags`
  // (string array). The first tag (if any) is shown as a row-level label.
  const tagsLabel = Array.isArray(note.tags) && note.tags.length
    ? note.tags.map(t => (typeof t === 'string' ? t : t.name)).filter(Boolean).join(', ')
    : null

  return (
    <div style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          padding: '12px 1.5rem',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div
          className='font-size-xs' style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            color: 'var(--text-color-subtle)'
          }}
        >
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {linkedContactName(note) || 'Unlinked'}
            {tagsLabel ? ` • ${tagsLabel}` : ''}
          </div>
          {dateFormatted ? <div style={{ flexShrink: 0 }}>{dateFormatted}</div> : null}
        </div>
        <div
          className='font-size-sm' style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {note.content
            ? (
              // Wealthbox stores note bodies as HTML (notes always come back
              // wrapped in at least a <div>; can include <br>, <p>, <a>, lists,
              // formatting). Use MarkupPreview to strip + sanitize for the
              // row preview - the 2-line CSS clamp above still applies.
              <MarkupPreview
                value={note.content}
                type='html'
                length={previewLength}
                style={{ fontWeight: 'inherit' }}
              />
            )
            : <span style={{ color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>Empty note</span>}
        </div>
      </div>
    </div>
  )
}

WealthboxNoteRow.propTypes = {
  note: PropTypes.object.isRequired,
  previewLength: PropTypes.number,
  onClick: PropTypes.func.isRequired
}

export default WealthboxNoteRow
